import { useState } from "react";
import { useToast } from "../context/ToastContext";
import { getSessionToken } from "../hooks/useSession";
import { AgentSearchSelect } from "./fields/AgentSearchSelect";
import { Button } from "./ui/Button";
import { Input, Label, FieldHelp } from "./ui/Input";
import { ModalOverlay } from "./ui/Modal";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

export function CreateAgentModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { error: toastError, success: toastSuccess } = useToast();
  const token = getSessionToken();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [npn, setNpn] = useState("");
  const [uplineUserId, setUplineUserId] = useState("");
  const [sendActivationEmail, setSendActivationEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!token) return;
    if (!firstName.trim() && !lastName.trim()) {
      toastError("Enter a first or last name.");
      return;
    }
    if (sendActivationEmail && !email.trim()) {
      toastError("An email address is required to send an activation email.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/agents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          email: email.trim() || undefined,
          npn: npn.trim() || undefined,
          uplineUserId: uplineUserId || undefined,
          sendActivationEmail,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to create agent");
      toastSuccess(
        sendActivationEmail ? "Agent created and activation email sent." : "Agent created."
      );
      onCreated();
      onClose();
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Failed to create agent");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} ariaLabel="Create a new agent">
      <h2 className="mb-1 text-lg font-semibold text-content-primary">New agent</h2>
      <p className="mb-4 text-sm text-content-secondary">
        Register an agent directly so they can be set as an upline, without waiting on the
        upstream data scrapes. This does not send a portal invite unless you check the box below.
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Label>
            First name
            <Input
              className="mt-1.5"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoFocus
            />
          </Label>
          <Label>
            Last name
            <Input
              className="mt-1.5"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Label>
        </div>

        <Label>
          Email
          <Input
            className="mt-1.5"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Optional unless sending an activation email"
          />
        </Label>

        <Label>
          NPN
          <Input
            className="mt-1.5 font-mono"
            value={npn}
            onChange={(e) => setNpn(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </Label>

        <Label>
          Upline
          <div className="mt-1.5">
            <AgentSearchSelect
              value={uplineUserId}
              token={token ?? ""}
              onChange={setUplineUserId}
              placeholder="No upline"
              clearLabel="No upline"
            />
          </div>
        </Label>

        <label className="flex items-start gap-2 text-sm text-content-primary">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={sendActivationEmail}
            onChange={(e) => setSendActivationEmail(e.target.checked)}
          />
          <span>
            Send activation email
            <FieldHelp className="mt-0.5">
              Enables portal login and emails the agent a sign-in link. Leave unchecked to just
              register them as an agent record.
            </FieldHelp>
          </span>
        </label>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Creating…" : "Create agent"}
        </Button>
      </div>
    </ModalOverlay>
  );
}
